/** @jsxImportSource preact */
import type { VNode } from "preact";

import * as m from "../../paraglide/messages.js";
import { FormButton } from "../form/form-button";
import { FORM_STYLES, formVars } from "../form/form-styles";
import {
  CheckIcon,
  RefreshIcon,
  SpinnerIcon,
  TriangleAlertIcon,
} from "../icons";
import type { WidgetTokens } from "../theme";

/** 已受理的请求：浮层只说商家会回复到哪个邮箱，编号和时间写成一行小字。 */
export interface RequestReceipt {
  /** 请求编号，如 `GS-0001`，原文由后端给。 */
  requestId: string;
  /** 买家填的联系邮箱，商家回复发到这里。 */
  contactEmail: string;
  /** 已按买家语言格式化的受理时间。 */
  time?: string;
}

/**
 * 请求浮层里不放表单的几步。受理之后由商家通过邮件回复买家，挂件不跟踪送达，所以这里没有已送达、退信这类状态。
 * already_sent：同一个问题再次要求联系商家时，先给已经发过的那一条。
 */
export type RequestStatusState =
  | { kind: "loading" }
  | { kind: "sending" }
  | { kind: "accepted"; receipt: RequestReceipt }
  | { kind: "already_sent"; receipt: RequestReceipt }
  | { kind: "load_failed" }
  | { kind: "send_failed" };

export interface RequestStatusProps {
  state: RequestStatusState;
  tokens: WidgetTokens;
  /** 标题的元素 id：浮层没有标题行时，读屏用它给对话框命名。 */
  titleId?: string;
  /** 已受理、已发过时的「Back to conversation」。 */
  onBack?: () => void;
  /** 两种失败时的「Try again」。 */
  onRetry?: () => void;
  /** 已发过时的「Start a new request」。 */
  onStartNew?: () => void;
  /** 发送失败时的「Edit details」，回到表单改了再发。 */
  onEdit?: () => void;
}

/** progress 白色纸面圆；done 墨黑实心圆（同 Sent 卡的对勾）；problem 危险色淡底圆。 */
type Tone = "done" | "problem" | "progress";

export const REQUEST_STATUS_STYLES = `
.gn-request-status { display:flex; flex-direction:column; gap:24px; padding-top:24px; color:var(--gn-form-ink); }
.gn-request-status-main { display:flex; flex-direction:column; align-items:center; text-align:center; }
.gn-request-status-icon { flex:none; box-sizing:border-box; display:flex; align-items:center; justify-content:center; width:56px; height:56px; border-radius:var(--gn-form-r-pill); background:var(--gn-form-sheet); color:var(--gn-form-ink); box-shadow:var(--gn-form-shadow-pill); }
.gn-request-status[data-tone="done"] .gn-request-status-icon { background:var(--gn-form-answer); color:var(--gn-form-answer-fg); box-shadow:none; }
.gn-request-status[data-tone="problem"] .gn-request-status-icon { background:color-mix(in srgb, var(--gn-form-danger) 12%, var(--gn-form-sheet)); color:var(--gn-form-danger); box-shadow:none; }
.gn-request-status-title { margin:16px 0 0; font-size:17px; font-weight:700; letter-spacing:-.01em; line-height:1.3; }
.gn-request-status-text { margin:6px 0 0; max-width:280px; font-size:13px; line-height:1.5; color:var(--gn-form-quiet); }
.gn-request-status-meta { margin:12px 0 0; font-size:12px; line-height:1.4; color:var(--gn-form-quiet); font-variant-numeric:tabular-nums; }
.gn-request-status-actions { display:flex; flex-direction:column; gap:8px; }
`;

interface View {
  tone: Tone;
  icon: VNode;
  title: string;
  text: string;
  meta?: string;
}

function receiptMeta({ requestId, time }: RequestReceipt): string {
  return time
    ? m.widget_request_meta_time({ id: requestId, time })
    : m.widget_request_meta({ id: requestId });
}

function view(state: RequestStatusState): View {
  const spinner = <SpinnerIcon size={26} />;
  const check = <CheckIcon size={26} strokeWidth={2.6} />;
  const alert = <TriangleAlertIcon size={24} strokeWidth={2.2} />;
  switch (state.kind) {
    case "loading":
      return {
        tone: "progress",
        icon: spinner,
        title: m.widget_request_loading_title(),
        text: m.widget_request_loading_text(),
      };
    case "sending":
      return {
        tone: "progress",
        icon: spinner,
        title: m.widget_request_sending_title(),
        text: m.widget_request_sending_text(),
      };
    case "accepted":
      return {
        tone: "done",
        icon: check,
        title: m.widget_email_result_title_accepted(),
        text: m.widget_email_result_next_reply({
          email: state.receipt.contactEmail,
        }),
        meta: receiptMeta(state.receipt),
      };
    case "already_sent":
      return {
        tone: "done",
        icon: check,
        title: m.widget_request_already_sent_title(),
        text: m.widget_email_result_next_reply({
          email: state.receipt.contactEmail,
        }),
        meta: receiptMeta(state.receipt),
      };
    case "load_failed":
      return {
        tone: "problem",
        icon: alert,
        title: m.widget_request_load_failed_title(),
        text: m.widget_request_load_failed_text(),
      };
    case "send_failed":
      return {
        tone: "problem",
        icon: alert,
        title: m.widget_email_result_title_failed(),
        text: m.widget_request_send_failed_text(),
      };
  }
}

/**
 * 请求浮层里的一步状态：居中的图标圆、标题、一两句说明，下面是整宽按钮。不套卡片，直接放在浮层底色上；
 * 浮层右上角的关闭键由外面给。状态变化时礼貌播报，出了问题时用 alert。
 */
export function RequestStatus({
  state,
  tokens: t,
  titleId,
  onBack,
  onRetry,
  onStartNew,
  onEdit,
}: RequestStatusProps) {
  const { tone, icon, title, text, meta } = view(state);
  const done = state.kind === "accepted" || state.kind === "already_sent";
  const failed = state.kind === "load_failed" || state.kind === "send_failed";
  const actions = [
    done && onBack && (
      <FormButton fullWidth key="back" onClick={onBack} tokens={t}>
        {m.widget_request_back()}
      </FormButton>
    ),
    state.kind === "already_sent" && onStartNew && (
      <FormButton
        fullWidth
        key="new"
        onClick={onStartNew}
        tokens={t}
        variant="secondary"
      >
        {m.widget_email_result_start_new()}
      </FormButton>
    ),
    failed && onRetry && (
      <FormButton fullWidth key="retry" onClick={onRetry} tokens={t}>
        <RefreshIcon size={14} />
        {m.widget_request_try_again()}
      </FormButton>
    ),
    state.kind === "send_failed" && onEdit && (
      <FormButton
        fullWidth
        key="edit"
        onClick={onEdit}
        tokens={t}
        variant="secondary"
      >
        {m.widget_email_result_edit()}
      </FormButton>
    ),
  ].filter(Boolean);
  return (
    <div class="gn-request-status" data-tone={tone} style={formVars(t)}>
      <style>{`${FORM_STYLES}${REQUEST_STATUS_STYLES}`}</style>
      <div
        aria-live={tone === "problem" ? undefined : "polite"}
        class="gn-request-status-main"
        role={tone === "problem" ? "alert" : "status"}
      >
        <span aria-hidden="true" class="gn-request-status-icon">
          {icon}
        </span>
        <h2 class="gn-request-status-title" id={titleId}>
          {title}
        </h2>
        <p class="gn-request-status-text">{text}</p>
        {meta && <p class="gn-request-status-meta">{meta}</p>}
      </div>
      {actions.length > 0 && (
        <div class="gn-request-status-actions">{actions}</div>
      )}
    </div>
  );
}
